/*
 * @Author: yangtianyu01 yangtianyu01@corp.netease.com
 * @Date: 2025-05-23 15:32:28
 * @LastEditors: CN\yangtianyu01 yangtianyu01@corp.netease.com
 * @LastEditTime: 2026-02-02 15:14:09
 * @FilePath: \MPC-monorepo\features\mpc-common\src\components\ProTablePlusForm\index.tsx
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from 'react';
import { ProTablePlus } from '@bedrock/eed-react';
import { IProTableRef } from '@bedrock/eed-react/lib/components/ProTable/interface';
import { useMemoizedFn, useControllableValue, useDebounceFn } from 'ahooks';
import { useAddGroupTableData } from './addGroupTable';
import type { AddGroupFormColumn } from './addGroupTable';
import cx from 'classnames';
import './index.less';
import './TableExtend';
import {
  tableColumnWidthToRem,
  createIsEqualWithIgnore,
  getDiff,
  tableColumnWidthViewBase,
} from '@mpc/common/src/utils';

export type ProTablePlusFormProps = Omit<
  React.ComponentProps<typeof ProTablePlus>,
  'data' | 'columns'
> & {
  columns: AddGroupFormColumn[];
  value: any[];
  onChange: (value: any[]) => void;
  onBatchEditChange?: (newData: any[]) => void;
  readOnly?: boolean;
  ignoreCompareKeys?: string[];
  onFirstTreeReady?: (data) => void;
};

const isEqualWithIgnore = createIsEqualWithIgnore([
  '__table_row_key__',
  'parent',
]);

export type ProTablePlusFormRef = React.RefAttributes<typeof ProTablePlus> & {
  validateFields: () => Promise<any>;
  validateFieldsAndScroll: () => Promise<any>;
  /**
   * 滚动到指定单元格。运行时由内部 table ref（@bedrock table）经 useImperativeHandle
   * 的 `...tableRef.current` 透传，故此处显式声明其类型，供上层校验后定位到出错单元格。
   * 可选：首次渲染（内部 table 尚未就绪）时 ref 为空对象，调用方需用可选链。
   */
  scrollTo?: (opt: { key: string; dataIndex: string }) => void;
  treeInitData: (data: any[], columns?: any[]) => void;
  treeUpdateTableData: (data: any[], columns: any[], rowKey: string) => void;
  treeSetRowById: (id: string, row: any) => void;
  treeAddToParent: (id: string, row: any, toParentId?: string) => void;
  treeCopyRowById: (id: string, row: any) => void;
  treeDeleteRowById: (id: string) => void;
  treeDeleteRowByIds: (id: string[]) => void;
  getIsVirtualScroll: () => boolean;
};
export const ProTablePlusForm = forwardRef<
  ProTablePlusFormRef,
  ProTablePlusFormProps & { viewBase?: number }
>((props, ref) => {
  const [data, setData] = useControllableValue(props, {});
  const {
    onDataChange,
    columns,
    onBatchEditChange,
    readOnly = false,
    ignoreCompareKeys = [],
    viewBase,
    onFirstTreeReady,
    ...rest
  } = props;
  const tableRef = useRef<IProTableRef<any>>(null);
  const [showData, setShowData] = useState(data);
  const [hasValidate, setHasValidate] = useState(false);
  const localData = useRef(data);
  const hasInit = useRef(false);

  const setLocalData = useMemoizedFn((newData) => {
    if (isEqualWithIgnore(localData.current, newData)) return;
    localData.current = newData;
    setShowData(newData);
    setData(newData);
  });

  const {
    treeReady,
    isTree,
    columns: addGroupColumns,
    treeSetRowById,
    treeAddToParent,
    treeCopyRowById,
    treeDeleteRowById,
    treeDeleteRowByIds,
    treeUpdateTableData,
    treeInitData,
  } = useAddGroupTableData(showData, setLocalData, columns, readOnly);

  useEffect(() => {
    if (treeReady && !hasInit.current) {
      hasInit.current = true;
      onFirstTreeReady?.(showData);
    }
  }, [treeReady, showData]);

  const isVirtualScroll = useMemo(() => {
    if (rest?.virtual?.maxRows <= 0) return true;
    return rest?.virtual?.maxRows && showData?.length > rest?.virtual?.maxRows;
  }, [rest?.virtual?.maxRows, showData]);

  const scrollTop = useRef(0);

  const validateFields = useMemoizedFn(() => {
    return new Promise((resolve, reject) => {
      setHasValidate(true);
      tableRef.current.validate().then((err, values) => {
        if (err?.length) {
          reject(err);
        } else {
          resolve(values);
        }
      });
    });
  });

  // 虚拟滚动场景下需要根据
  const allValidate = useMemoizedFn(() => {
    // 判断是否触发虚拟滚动
    if (!isVirtualScroll) {
      return tableRef.current.validate();
    }
    const vMap = tableRef.current.getValidateRulesMap('all') as Map<
      string,
      any[]
    >;
    return Promise.all(
      Array.from(vMap.values()).map((v: any) => {
        return new Promise((resolve, reject) => {
          v?.doValidate?.().then((n) => {
            resolve(n);
          });
        });
      })
    ).then((n) => n.filter((v) => !!v));
  });

  const validateFieldsAndScroll = useMemoizedFn(() => {
    return new Promise((resolve, reject) => {
      setHasValidate(true);
      allValidate().then((err, values) => {
        if (err?.length) {
          reject(err);
          tableRef.current.scrollTo({
            key: err[0].rowKey,
            dataIndex: err[0].dataIndex,
          });
          setTimeout(() => {
            tableRef.current?.validate?.();
          }, 10);
        } else {
          resolve(values);
        }
      });
    });
  });

  // 防抖validate
  const { run: debounceValidate } = useDebounceFn(
    () => {
      tableRef?.current?.validate?.();
    },
    {
      wait: 40,
      leading: false,
      trailing: true,
    }
  );

  const tempOnScroll = useMemoizedFn((e) => {
    rest?.onScroll?.(e);
    // 判断只有垂直滚动时才进行校验
    if (hasValidate && isVirtualScroll) {
      debounceValidate();
    }
  });

  const getIsVirtualScroll = useMemoizedFn(() => {
    return isVirtualScroll;
  });

  useImperativeHandle(ref, () => {
    if (tableRef?.current?.validate) {
      return {
        ...tableRef.current,
        treeReady,
        validateFields,
        validateFieldsAndScroll,
        treeInitData,
        treeUpdateTableData,
        treeSetRowById,
        treeAddToParent,
        treeCopyRowById,
        treeDeleteRowById,
        treeDeleteRowByIds,
        getIsVirtualScroll,
      } as any;
    }
    return {} as ProTablePlusFormRef;
  });

  useEffect(() => {
    // 非树形数据同步
    if (!isTree) {
      if (!isEqualWithIgnore(data, tableRef.current.getData())) {
        setLocalData(data);
      }
    }
  }, [data, isTree, isEqualWithIgnore]);

  const onDataChangeTemp = useMemoizedFn((newData, changeData) => {
    onDataChange?.(newData, changeData);
    // 非树形数据同步
    if (!isTree) {
      if (!isEqualWithIgnore(newData, data)) {
        setData(newData);
      }
    }
  });

  const onBatchEditChangeTemp = useMemoizedFn((newData) => {
    onBatchEditChange?.(newData);
    if (isTree) {
      treeInitData(tableRef.current.getData(), columns);
    }
  });

  // console.log(3333, showData);

  return (
    treeReady && (
      <ProTablePlus
        ref={tableRef}
        data={showData}
        onDataChange={onDataChangeTemp}
        columns={addGroupColumns.map((item) =>
          viewBase
            ? tableColumnWidthViewBase(item, viewBase)
            : tableColumnWidthToRem(item)
        )}
        onBatchEditChange={onBatchEditChangeTemp}
        emptyText={
          <div style={{ textAlign: 'center', color: '#878A99' }}>暂无数据</div>
        }
        {...rest}
        containerClassName={cx('pro-table-form', rest?.containerClassName)}
        onScroll={tempOnScroll}
      />
    )
  );
});
